import { FlexCol } from '@/src/components'
import { ActionModal } from '@/src/components/ActionModal'
import { BaseText } from '@/src/components/BaseText'
import { FormSelect } from '@/src/components/Form/FormSelect'
import { BaseButton } from '@/src/components/buttons/BaseButton'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { useBaseToast } from '@/src/hooks/useBaseToast'
import { useIsCustomReferenceBaseLoadEnabled } from '@/src/shared/feature-flag/feature-flag.hooks'
import { isFileFormatAllowed } from '@/src/utils/fileFormatUtil'
import { Box, Spinner } from '@chakra-ui/react'
import {
  useLoadPlanningEarliestConsumption,
  useLoadedStrategyMetadata,
} from '@features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import { getLoadPlanningEnergyTypeLabel } from '@features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { type ChangeEvent, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiUpload } from 'react-icons/fi'
import { LuGalleryHorizontal, LuRefreshCw } from 'react-icons/lu'
import { type MenuListProps, type MenuProps, type OptionProps, components as selectComponents } from 'react-select'
import { useCreatePlannedStrategy, useUpdatePlannedStrategy } from '../../details/hooks/usePlannedStrategies.hook'
import { mapToEnergyTypeDto } from '../../details/types/adjustment.utils'
import { useLoadPlanning } from '../../hooks/useLoadPlanningsV2.hook'
import {
  getCustomReferenceBaseLoadUploadErrorMessage,
  useDeleteCustomReferenceBaseLoad,
  useUploadCustomReferenceBaseLoad,
} from '../hooks/useUploadCustomReferenceBaseLoad.hooks'
import { getMinLatestReferenceDateTime } from '../load-planning-adjustments/ReferencePeriodAdjustment.utils'
import {
  LAST_365_DAYS,
  LAST_365_DAYS_UPDATED,
  type ReferenceTimePeriodOption,
  getOptionValueFromReferenceDate,
  getOrderedReferenceTimePeriodOptions,
  getReferencePeriodLabel,
  getReferenceRequestDateFromOptionValue,
  getReferenceTimePeriodOptions,
  getReferenceYearsFromEarliestConsumption,
  getUpToDateLast365PeriodLabel,
  isOutdatedLast365ReferenceDate,
  isYearOptionValue,
} from './ReferenceTimePeriod.utils'
import { ReferenceTimePeriodCustomBaseLoadUpload } from './ReferenceTimePeriodCustomBaseLoadUpload'
import { ReferenceTimePeriodOptionLabel } from './ReferenceTimePeriodOptionLabel'
import { ReferenceTimePeriodOutdatedInfo } from './ReferenceTimePeriodOutdatedInfo'
import { ReferenceTimePeriodWarning } from './ReferenceTimePeriodWarning'
import { translateCustomReferenceBaseLoadUploadError } from './customReferenceBaseLoadUploadError.utils'

interface ReferenceTimePeriodModalProps {
  isOpen: boolean
  onClose: () => void
  referenceDate?: string
  isRefreshingLast365?: boolean
  updatingReferenceDateForFirstTime?: boolean
  showCustomBaseLoad?: boolean
  hasStoredCustomBaseLoad?: boolean
}

const referencePeriodSelectComponents = {
  Menu: (props: MenuProps) => (
    <selectComponents.Menu
      {...props}
      innerProps={{
        ...props.innerProps,
        style: { ...props.innerProps.style, overflow: 'visible', overflowX: 'visible' },
      }}
    />
  ),
  MenuList: (props: MenuListProps) => (
    <selectComponents.MenuList
      {...props}
      innerProps={{
        ...props.innerProps,
        style: { ...props.innerProps.style, overflow: 'visible', overflowX: 'visible' },
      }}
    />
  ),
  Option: (props: OptionProps) => (
    <selectComponents.Option
      {...props}
      innerProps={{
        ...props.innerProps,
        style: { ...props.innerProps.style, padding: 0 },
      }}
    />
  ),
}

export const ReferenceTimePeriodModal = ({
  isOpen,
  onClose,
  referenceDate,
  updatingReferenceDateForFirstTime,
  showCustomBaseLoad = false,
  hasStoredCustomBaseLoad = false,
  isRefreshingLast365 = false,
}: ReferenceTimePeriodModalProps) => {
  const { t } = useTranslation()
  const toast = useBaseToast()
  const isCustomReferenceBaseLoadEnabled = useIsCustomReferenceBaseLoadEnabled()
  const canUploadCustomBaseLoad = showCustomBaseLoad && isCustomReferenceBaseLoadEnabled
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const loadPlanningYear = Number(strategyMetadata?.year)
  const [userSelectedOption, setUserSelectedOption] = useState<string | null>(null)
  const [customBaseLoadFile, setCustomBaseLoadFile] = useState<File | null>(null)
  const [removedStoredCustomBaseLoad, setRemovedStoredCustomBaseLoad] = useState(false)
  const customBaseLoadInputRef = useRef<HTMLInputElement>(null)

  const { locations, isAllLocation, companyPgId: companyId, isInitialized: isLocationsInitialized } = useLocationsContext()

  const { data: earliestConsumption } = useLoadPlanningEarliestConsumption({
    companyId: companyId!,
    energyType: mapToEnergyTypeDto(strategyMetadata?.energyType),
    year: loadPlanningYear,
  })

  const mainLocation = locations?.find(isAllLocation)

  const createPlannedStrategy = useCreatePlannedStrategy()
  const updatePlannedStrategy = useUpdatePlannedStrategy()
  const uploadCustomReferenceBaseLoad = useUploadCustomReferenceBaseLoad()
  const deleteCustomReferenceBaseLoad = useDeleteCustomReferenceBaseLoad()
  const isUsingStoredCustomBaseLoad = hasStoredCustomBaseLoad && !removedStoredCustomBaseLoad && !customBaseLoadFile
  const isCustomBaseLoadActive = Boolean(customBaseLoadFile) || isUsingStoredCustomBaseLoad
  const isSavingReferencePeriod =
    createPlannedStrategy.isPending ||
    updatePlannedStrategy.isPending ||
    uploadCustomReferenceBaseLoad.isPending ||
    deleteCustomReferenceBaseLoad.isPending

  const referenceYears = useMemo(() => getReferenceYearsFromEarliestConsumption(earliestConsumption), [earliestConsumption])

  const latestReferenceDateTime = useMemo(() => getMinLatestReferenceDateTime(earliestConsumption), [earliestConsumption])

  const includedLocationIds = useMemo(() => {
    if (!earliestConsumption) return []
    return earliestConsumption.map(entry => Number(entry.locationId))
  }, [earliestConsumption])

  const energyType = mapToEnergyTypeDto(strategyMetadata?.energyType)

  const options = useMemo(
    () => getReferenceTimePeriodOptions(t, referenceDate, referenceYears, latestReferenceDateTime),
    [t, referenceDate, referenceYears, latestReferenceDateTime]
  )

  const defaultReferenceOptionValue = useMemo(() => {
    const candidate =
      isRefreshingLast365 || !referenceDate ? LAST_365_DAYS : getOptionValueFromReferenceDate(referenceDate, latestReferenceDateTime)
    if (!latestReferenceDateTime) return candidate
    if (options.some(option => option.value === candidate)) return candidate
    return options[0]?.value ?? candidate
  }, [latestReferenceDateTime, isRefreshingLast365, referenceDate, options])

  const selectedReferenceOptionValue = userSelectedOption ?? defaultReferenceOptionValue

  const orderedOptions = useMemo(
    () => getOrderedReferenceTimePeriodOptions(options, selectedReferenceOptionValue),
    [options, selectedReferenceOptionValue]
  )

  const initialReferenceOptionValue = useMemo(
    () => getOptionValueFromReferenceDate(referenceDate, latestReferenceDateTime),
    [referenceDate, latestReferenceDateTime]
  )

  const isReferenceDateChanged = initialReferenceOptionValue !== selectedReferenceOptionValue

  const shouldShowWarning = !updatingReferenceDateForFirstTime && (isRefreshingLast365 || isReferenceDateChanged)
  const isOutdatedLast365Selected =
    selectedReferenceOptionValue === LAST_365_DAYS_UPDATED && isOutdatedLast365ReferenceDate(referenceDate, latestReferenceDateTime)
  const shouldShowOutdatedLast365Info = !updatingReferenceDateForFirstTime && !isRefreshingLast365 && isOutdatedLast365Selected

  const getWarningPeriodLabelByValue = (value?: string) => {
    if (!value) return '-'
    if (isYearOptionValue(value)) {
      return t('components.procurement.forecasting.reference_period_modal.year_label', { year: value })
    }
    if (value === LAST_365_DAYS) return getUpToDateLast365PeriodLabel(latestReferenceDateTime)
    if (value === LAST_365_DAYS_UPDATED) return getReferencePeriodLabel(referenceDate)
    return '-'
  }

  const newPeriodLabel = useMemo(() => getWarningPeriodLabelByValue(selectedReferenceOptionValue), [selectedReferenceOptionValue])

  const oldPeriodLabel = useMemo(() => {
    const currentValue = getOptionValueFromReferenceDate(referenceDate, latestReferenceDateTime)
    return getWarningPeriodLabelByValue(currentValue)
  }, [referenceDate, latestReferenceDateTime])

  const { isFetching } = useLoadPlanning(companyId, strategyMetadata?.year, mapToEnergyTypeDto(strategyMetadata?.energyType))

  const handleClose = () => {
    setCustomBaseLoadFile(null)
    setRemovedStoredCustomBaseLoad(false)
    onClose()
  }

  const onCustomBaseLoadFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    event.target.value = ''
    if (!selectedFile) return

    if (!isFileFormatAllowed(selectedFile.name, ['.csv'])) {
      toast({
        status: 'error',
        title: t('components.procurement.forecasting.reference_period_modal.csv_only_error'),
      })
      return
    }

    setCustomBaseLoadFile(selectedFile)
  }

  const onSave = async () => {
    const rawLocationPgId = mainLocation?.rawLocationPgId

    if (!companyId || Number.isNaN(loadPlanningYear) || !rawLocationPgId) {
      toast({ status: 'error', title: t('components.procurement.forecasting.reference_period_modal.save_error_toast') })
      return
    }

    let nextReferenceDate: string
    try {
      nextReferenceDate = getReferenceRequestDateFromOptionValue(
        isCustomBaseLoadActive ? LAST_365_DAYS : selectedReferenceOptionValue,
        referenceDate,
        latestReferenceDateTime
      )
    } catch (error) {
      toast({ status: 'error', title: t('components.procurement.forecasting.reference_period_modal.save_error_toast') })
      return
    }

    if (canUploadCustomBaseLoad && customBaseLoadFile) {
      try {
        await uploadCustomReferenceBaseLoad.mutateAsync({
          companyId,
          year: loadPlanningYear.toString(),
          energyType,
          locationId: rawLocationPgId,
          referenceDate: nextReferenceDate,
          formData: {
            file: customBaseLoadFile,
          },
        })
        setCustomBaseLoadFile(null)
      } catch (error) {
        const uploadErrorMessage = translateCustomReferenceBaseLoadUploadError(getCustomReferenceBaseLoadUploadErrorMessage(error) ?? '', t)
        toast({
          status: 'error',
          title: t('components.procurement.forecasting.reference_period_modal.save_error_toast'),
          description: uploadErrorMessage ? <Box whiteSpace='pre-line'>{uploadErrorMessage}</Box> : undefined,
          duration: uploadErrorMessage ? null : undefined,
        })
        return
      }

      if (!updatingReferenceDateForFirstTime) {
        onClose()
      }

      toast({ status: 'success', title: t('components.procurement.forecasting.reference_period_modal.save_success_toast') })
      return
    }

    try {
      if (updatingReferenceDateForFirstTime) {
        await createPlannedStrategy.mutateAsync({
          requestBody: {
            companyId,
            locationId: rawLocationPgId,
            year: loadPlanningYear.toString(),
            energyType,
            referenceDate: nextReferenceDate,
          },
        })
      } else {
        await updatePlannedStrategy.mutateAsync({
          companyId,
          year: loadPlanningYear,
          energyType,
          requestBody: {
            referenceDate: nextReferenceDate,
          },
        })
      }
    } catch (error) {
      toast({ status: 'error', title: t('components.procurement.forecasting.reference_period_modal.save_error_toast') })
      return
    }

    let customBaseLoadRemovalFailed = false

    if (hasStoredCustomBaseLoad && removedStoredCustomBaseLoad) {
      try {
        await deleteCustomReferenceBaseLoad.mutateAsync({
          companyId,
          year: loadPlanningYear.toString(),
          energyType,
        })
      } catch (error) {
        customBaseLoadRemovalFailed = true
      }
    }

    if (!updatingReferenceDateForFirstTime) {
      onClose()
    }

    toast({
      status: customBaseLoadRemovalFailed ? 'error' : 'success',
      title: customBaseLoadRemovalFailed
        ? t('components.procurement.forecasting.reference_period_modal.remove_custom_base_load_error')
        : t('components.procurement.forecasting.reference_period_modal.save_success_toast'),
    })
  }

  return (
    <ActionModal
      actionButtonText={
        canUploadCustomBaseLoad && customBaseLoadFile
          ? t('components.procurement.forecasting.reference_period_modal.save_button_file')
          : isRefreshingLast365
            ? t('components.procurement.forecasting.reference_period_modal.refresh_save_button')
            : updatingReferenceDateForFirstTime
              ? t('components.procurement.forecasting.reference_period_modal.save_button_initial')
              : t('components.procurement.forecasting.reference_period_modal.save_button')
      }
      actionButtonIconLeft={
        isSavingReferencePeriod ? (
          <Spinner size='sm' thickness='2px' />
        ) : canUploadCustomBaseLoad && customBaseLoadFile ? (
          <FiUpload size={16} />
        ) : isRefreshingLast365 ? (
          <LuRefreshCw size={16} />
        ) : (
          <LuGalleryHorizontal size={16} />
        )
      }
      size={'xl'}
      header={`${t('components.procurement.forecasting.reference_period_modal.heading', { year: loadPlanningYear, energyType: getLoadPlanningEnergyTypeLabel(t, strategyMetadata?.energyType) })}`}
      isOpen={isOpen}
      isCentered
      onSave={onSave}
      onClose={handleClose}
      isSaveDisabled={isSavingReferencePeriod || isFetching || !isLocationsInitialized}
      cancelButtonDataAction={
        updatingReferenceDateForFirstTime ? 'initial-loadplan-reference-modal-cancel-action' : 'cancel-timeframe-adjustment'
      }
      saveButtonDataAction={
        updatingReferenceDateForFirstTime
          ? 'initial-loadplan-reference-modal-confirm'
          : isRefreshingLast365
            ? 'refreshsave-timeframe-loadplanning'
            : 'save-timeframe-loadplanning'
      }
      closeButtonDataAction={updatingReferenceDateForFirstTime ? 'initial-loadplan-reference-modal-closex' : undefined}
      minWidth='600px'
    >
      <FlexCol mt={'-12px'} mb={'-32px'}>
        <BaseText whiteSpace='pre-line'>{t('components.procurement.forecasting.reference_period_modal.description')}</BaseText>
        {shouldShowOutdatedLast365Info && !isCustomBaseLoadActive ? <ReferenceTimePeriodOutdatedInfo /> : null}
        {shouldShowWarning ? (
          <ReferenceTimePeriodWarning
            oldPeriodLabel={oldPeriodLabel}
            newPeriodLabel={newPeriodLabel}
            isCustomBaseLoad={isCustomBaseLoadActive}
          />
        ) : null}
        {!isRefreshingLast365 ? (
          <Box mt='16px'>
            {!isCustomBaseLoadActive ? (
              <FormSelect
                value={selectedReferenceOptionValue}
                name={'reference-period'}
                label={t('components.procurement.forecasting.reference_period_modal.select_label')}
                options={orderedOptions}
                hideSelectedOptions={false}
                anchor={document.body}
                components={referencePeriodSelectComponents}
                data-action={updatingReferenceDateForFirstTime ? 'initial-loadplan-reference-modal-select' : 'timeframe-select'}
                formatOptionLabel={(option, { context }) => {
                  const typedOption = option as ReferenceTimePeriodOption
                  if (context === 'value') return typedOption.label

                  return (
                    <ReferenceTimePeriodOptionLabel
                      label={typedOption.label}
                      optionValue={typedOption.value}
                      tagLabel={typedOption.tagLabel}
                      tagVariant={typedOption.tagVariant}
                      startDate={typedOption.startDate}
                      endDate={typedOption.endDate}
                      locationIds={includedLocationIds}
                      energyType={energyType}
                    />
                  )
                }}
                onChange={value => {
                  setUserSelectedOption(String(value))
                }}
              />
            ) : null}
            {canUploadCustomBaseLoad && !isUsingStoredCustomBaseLoad ? (
              <ReferenceTimePeriodCustomBaseLoadUpload
                customBaseLoadFile={customBaseLoadFile}
                fileInputRef={customBaseLoadInputRef}
                onFileChange={onCustomBaseLoadFileChange}
                onRemoveSelectedFile={() => setCustomBaseLoadFile(null)}
              />
            ) : null}
            {isUsingStoredCustomBaseLoad ? (
              <FlexCol mt='12px' gap='12px'>
                <BaseText textAlign='center'>
                  {t('components.procurement.forecasting.reference_period_modal.using_custom_base_load')}
                </BaseText>
                <BaseButton variant='secondary' width='100%' onClick={() => setRemovedStoredCustomBaseLoad(true)}>
                  {t('components.procurement.forecasting.reference_period_modal.remove_custom_base_load')}
                </BaseButton>
              </FlexCol>
            ) : null}
          </Box>
        ) : null}
      </FlexCol>
    </ActionModal>
  )
}
